import type {
  Difficulty,
  Exercise,
  ExerciseMeasure,
  ExerciseNote,
  KeySignature,
  TimeSignature,
} from "@/lib/exerciseGenerator";

export interface CxmlImportOptions {
  file: File;
  difficulty: Difficulty;
  tempoHint?: number;
  startBar?: number | null;
  endBar?: number | null;
}

type StaffClef = "treble" | "bass";

interface MeasureBucket {
  treble: ExerciseNote[];
  bass: ExerciseNote[];
}

const STEP_TO_SEMITONE: Record<string, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

const FIFTHS_TO_KEY: Record<number, KeySignature> = {
  [-4]: "Ab",
  [-3]: "Eb",
  [-2]: "Bb",
  [-1]: "F",
  [0]: "C",
  [1]: "G",
  [2]: "D",
  [3]: "A",
  [4]: "E",
};

function getChild(parent: Element, name: string): Element | null {
  const lower = name.toLowerCase();
  return Array.from(parent.children).find((el) => el.tagName.toLowerCase() === lower) ?? null;
}

function getChildren(parent: Element, name: string): Element[] {
  const lower = name.toLowerCase();
  return Array.from(parent.children).filter((el) => el.tagName.toLowerCase() === lower);
}

function getChildText(parent: Element, name: string): string | undefined {
  const child = getChild(parent, name);
  const value = child?.textContent?.trim();
  return value ? value : undefined;
}

function parseIntSafe(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseFloatSafe(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function timeSignatureFromXml(beats: number, beatType: number): TimeSignature | null {
  const raw = `${beats}/${beatType}`;
  if (raw === "4/4" || raw === "3/4" || raw === "2/4" || raw === "6/8") {
    return raw;
  }
  return null;
}

function getBeatsPerMeasure(timeSignature: TimeSignature): number {
  switch (timeSignature) {
    case "4/4": return 4;
    case "3/4": return 3;
    case "2/4": return 2;
    case "6/8": return 6;
    default: return 4;
  }
}

function clampTempo(tempo: number): number {
  return Math.max(40, Math.min(220, Math.round(tempo)));
}

function quarterLengthToNotation(quarterLength: number): { duration: string; dotted: boolean } {
  const approx = (value: number, target: number, tolerance = 0.03) => Math.abs(value - target) <= tolerance;

  if (approx(quarterLength, 4)) return { duration: "w", dotted: false };
  if (approx(quarterLength, 3)) return { duration: "h", dotted: true };
  if (approx(quarterLength, 2)) return { duration: "h", dotted: false };
  if (approx(quarterLength, 1.5)) return { duration: "q", dotted: true };
  if (approx(quarterLength, 1)) return { duration: "q", dotted: false };
  if (approx(quarterLength, 0.75)) return { duration: "8", dotted: true };
  if (approx(quarterLength, 0.5)) return { duration: "8", dotted: false };
  if (approx(quarterLength, 0.375)) return { duration: "16", dotted: true };
  if (approx(quarterLength, 0.25)) return { duration: "16", dotted: false };

  if (quarterLength >= 2.5) return { duration: "h", dotted: true };
  if (quarterLength >= 1.75) return { duration: "h", dotted: false };
  if (quarterLength >= 1.25) return { duration: "q", dotted: true };
  if (quarterLength >= 0.75) return { duration: "q", dotted: false };
  if (quarterLength >= 0.35) return { duration: "8", dotted: false };
  return { duration: "16", dotted: false };
}

function typeToQuarterLength(type: string, dotCount: number): number {
  const base = (() => {
    switch (type) {
      case "whole": return 4;
      case "half": return 2;
      case "quarter": return 1;
      case "eighth": return 0.5;
      case "16th": return 0.25;
      case "32nd": return 0.125;
      default: return 1;
    }
  })();

  let factor = 1;
  let step = 0.5;
  for (let i = 0; i < dotCount; i++) {
    factor += step;
    step /= 2;
  }

  return base * factor;
}

function quarterToExerciseBeats(quarterLength: number, timeSignature: TimeSignature): number {
  return timeSignature === "6/8" ? quarterLength * 2 : quarterLength;
}

function midiFromPitch(noteEl: Element): number {
  const pitchEl = getChild(noteEl, "pitch");
  if (!pitchEl) return 60;

  const step = (getChildText(pitchEl, "step") ?? "C").toUpperCase();
  const octave = parseIntSafe(getChildText(pitchEl, "octave")) ?? 4;
  const alter = parseIntSafe(getChildText(pitchEl, "alter")) ?? 0;

  const semitone = STEP_TO_SEMITONE[step] ?? 0;
  const midi = (octave + 1) * 12 + semitone + alter;
  return Math.max(0, Math.min(127, midi));
}

function measureBucket(map: Map<number, MeasureBucket>, measureNumber: number): MeasureBucket {
  const existing = map.get(measureNumber);
  if (existing) return existing;
  const next = { treble: [], bass: [] };
  map.set(measureNumber, next);
  return next;
}

function createRestNote(beatsPerMeasure: number): ExerciseNote {
  const quarterLength = beatsPerMeasure === 6 ? 3 : beatsPerMeasure;
  const notation = quarterLengthToNotation(quarterLength);

  return {
    midi: 60,
    duration: notation.duration,
    beats: beatsPerMeasure,
    dotted: notation.dotted,
    rest: true,
    beatPosition: 0,
    globalBeatPosition: 0,
    expectedDurationMs: 0,
  };
}

function normalizeStartBar(value: number | null | undefined): number {
  if (!value || !Number.isFinite(value)) return 1;
  return Math.max(1, Math.floor(value));
}

function normalizeEndBar(value: number | null | undefined): number | null {
  if (!value || !Number.isFinite(value)) return null;
  return Math.max(1, Math.floor(value));
}

export async function importExerciseFromCxml(options: CxmlImportOptions): Promise<Exercise> {
  const xmlText = await options.file.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");

  if (doc.querySelector("parsererror")) {
    throw new Error("Invalid cXML file format.");
  }

  const parts = Array.from(doc.getElementsByTagName("part"));
  if (parts.length === 0) {
    throw new Error("No score parts found in cXML file.");
  }

  const buckets = new Map<number, MeasureBucket>();
  const clefUsage = new Set<StaffClef>();

  let parsedTimeSignature: TimeSignature = "4/4";
  let parsedKeySignature: KeySignature = "C";
  let parsedTempo = clampTempo(options.tempoHint ?? 84);

  for (const part of parts) {
    let divisions = 1;
    const staffClefs = new Map<number, StaffClef>();
    staffClefs.set(1, "treble");
    staffClefs.set(2, "bass");
    // The trainer is monophonic per staff: keep only the first voice seen on each staff.
    const primaryVoiceByStaff = new Map<number, string>();

    const measures = Array.from(part.getElementsByTagName("measure"));
    for (let idx = 0; idx < measures.length; idx++) {
      const measure = measures[idx];
      const parsedMeasureNo = parseIntSafe(measure.getAttribute("number") ?? undefined);
      const measureNumber = parsedMeasureNo ?? (idx + 1);
      const bucket = measureBucket(buckets, measureNumber);

      // MusicXML uses a single time cursor per part; <backup>/<forward> move it
      // (e.g. to rewind between voices or staves within a measure).
      let cursor = 0;
      let lastChordStart = 0;

      for (const child of Array.from(measure.children)) {
        const tag = child.tagName.toLowerCase();

        if (tag === "attributes") {
          const divisionsValue = parseIntSafe(getChildText(child, "divisions"));
          if (divisionsValue && divisionsValue > 0) divisions = divisionsValue;

          const keyEl = getChild(child, "key");
          const fifthsValue = parseIntSafe(keyEl ? getChildText(keyEl, "fifths") : undefined);
          if (fifthsValue !== null && FIFTHS_TO_KEY[fifthsValue] !== undefined) {
            parsedKeySignature = FIFTHS_TO_KEY[fifthsValue];
          }

          const timeEl = getChild(child, "time");
          if (timeEl) {
            const beats = parseIntSafe(getChildText(timeEl, "beats"));
            const beatType = parseIntSafe(getChildText(timeEl, "beat-type"));
            if (beats && beatType) {
              const parsed = timeSignatureFromXml(beats, beatType);
              if (parsed) parsedTimeSignature = parsed;
            }
          }

          const clefEls = getChildren(child, "clef");
          clefEls.forEach((clefEl) => {
            const number = parseIntSafe(clefEl.getAttribute("number") ?? undefined) ?? 1;
            const sign = (getChildText(clefEl, "sign") ?? "").toUpperCase();
            const parsed: StaffClef = sign === "F" ? "bass" : "treble";
            staffClefs.set(number, parsed);
          });

          continue;
        }

        if (tag === "direction") {
          const soundEl = getChild(child, "sound");
          const tempoValue = parseFloatSafe(soundEl?.getAttribute("tempo") ?? undefined);
          if (tempoValue && tempoValue > 0) {
            parsedTempo = clampTempo(tempoValue);
          }
          continue;
        }

        if (tag === "backup" || tag === "forward") {
          const moveDivisions = parseFloatSafe(getChildText(child, "duration"));
          if (moveDivisions && moveDivisions > 0) {
            const moveBeats = quarterToExerciseBeats(moveDivisions / divisions, parsedTimeSignature);
            cursor = tag === "backup" ? Math.max(0, cursor - moveBeats) : cursor + moveBeats;
          }
          continue;
        }

        if (tag !== "note") continue;

        const noteEl = child;
        if (getChild(noteEl, "grace")) continue;

        const staff = parseIntSafe(getChildText(noteEl, "staff")) ?? 1;
        const clef = staffClefs.get(staff) ?? (staff === 2 ? "bass" : "treble");

        const isRest = !!getChild(noteEl, "rest");
        const isChordTone = !!getChild(noteEl, "chord");

        let quarterLength: number;
        const durationDivisions = parseFloatSafe(getChildText(noteEl, "duration"));
        if (durationDivisions && durationDivisions > 0) {
          quarterLength = durationDivisions / divisions;
        } else {
          const type = getChildText(noteEl, "type") ?? "quarter";
          const dots = getChildren(noteEl, "dot").length;
          quarterLength = typeToQuarterLength(type, dots);
        }

        const beats = quarterToExerciseBeats(quarterLength, parsedTimeSignature);
        if (beats <= 0) continue;

        // Advance the shared cursor for every sounding note, even ones we
        // filter out below — document position must stay correct.
        const startBeat = isChordTone ? lastChordStart : cursor;
        if (!isChordTone) {
          lastChordStart = startBeat;
          cursor += beats;
        }

        const voice = getChildText(noteEl, "voice") ?? "1";
        const primaryVoice = primaryVoiceByStaff.get(staff);
        if (primaryVoice === undefined) {
          primaryVoiceByStaff.set(staff, voice);
        } else if (voice !== primaryVoice) {
          continue;
        }

        if (isChordTone) {
          continue;
        }

        // Prefer <tie> (sound) elements; fall back to <notations><tied> (notation).
        let tieEls = getChildren(noteEl, "tie");
        if (tieEls.length === 0) {
          const notations = getChild(noteEl, "notations");
          tieEls = notations ? getChildren(notations, "tied") : [];
        }
        const tieStart = !isRest && tieEls.some((el) => el.getAttribute("type") === "start");
        const tieStop = !isRest && tieEls.some((el) => el.getAttribute("type") === "stop");

        const notation = quarterLengthToNotation(quarterLength);
        const note: ExerciseNote = {
          midi: isRest ? 60 : midiFromPitch(noteEl),
          duration: notation.duration,
          beats,
          dotted: notation.dotted,
          rest: isRest,
          beatPosition: startBeat,
          globalBeatPosition: 0,
          expectedDurationMs: 0,
          tieStart,
          tieStop,
        };

        if (clef === "treble") {
          bucket.treble.push(note);
        } else {
          bucket.bass.push(note);
        }

        clefUsage.add(clef);
      }
    }
  }

  const availableMeasureNumbers = Array.from(buckets.keys()).sort((a, b) => a - b);
  if (availableMeasureNumbers.length === 0) {
    throw new Error("No measures were found in the cXML file.");
  }

  const startBar = normalizeStartBar(options.startBar);
  const endBar = normalizeEndBar(options.endBar);

  if (endBar !== null && endBar < startBar) {
    throw new Error("End bar must be greater than or equal to start bar.");
  }

  const selectedMeasureNumbers = availableMeasureNumbers.filter((measureNumber) => {
    if (measureNumber < startBar) return false;
    if (endBar !== null && measureNumber > endBar) return false;
    return true;
  });

  if (selectedMeasureNumbers.length === 0) {
    throw new Error("No measures found in the selected bar range.");
  }

  const hasTreble = selectedMeasureNumbers.some((m) => (buckets.get(m)?.treble.length ?? 0) > 0);
  const hasBass = selectedMeasureNumbers.some((m) => (buckets.get(m)?.bass.length ?? 0) > 0);

  const clef: Exercise["clef"] = hasTreble && hasBass
    ? "both"
    : hasBass
      ? "bass"
      : "treble";

  const beatsPerMeasure = getBeatsPerMeasure(parsedTimeSignature);
  const beatDurationMs = parsedTimeSignature === "6/8"
    ? (60000 / parsedTempo) / 3
    : (60000 / parsedTempo);

  const measures: ExerciseMeasure[] = [];
  let totalNotes = 0;

  selectedMeasureNumbers.forEach((measureNumber, index) => {
    const bucket = buckets.get(measureNumber)!;

    const applyTiming = (notes: ExerciseNote[]) =>
      [...notes]
        .sort((a, b) => a.beatPosition - b.beatPosition)
        .map((note) => {
          const next = { ...note };
          next.globalBeatPosition = index * beatsPerMeasure + next.beatPosition;
          next.expectedDurationMs = next.beats * beatDurationMs;
          return next;
        });

    if (clef === "both") {
      const trebleNotes = bucket.treble.length > 0
        ? applyTiming(bucket.treble)
        : applyTiming([createRestNote(beatsPerMeasure)]);
      const bassNotes = bucket.bass.length > 0
        ? applyTiming(bucket.bass)
        : applyTiming([createRestNote(beatsPerMeasure)]);

      measures.push({ clef: "treble", measureIndex: index, notes: trebleNotes });
      measures.push({ clef: "bass", measureIndex: index, notes: bassNotes });
      return;
    }

    const notesForClef = clef === "treble" ? bucket.treble : bucket.bass;
    const resolvedNotes = notesForClef.length > 0
      ? applyTiming(notesForClef)
      : applyTiming([createRestNote(beatsPerMeasure)]);

    measures.push({ clef, measureIndex: index, notes: resolvedNotes });
  });

  // Validate ties after bar slicing: a tieStop is only meaningful when the
  // immediately preceding playable note of the same clef started a tie on the
  // same pitch (a sliced range can begin mid-tie). Dangling stops become
  // normal playable notes so evaluation and rendering stay index-aligned.
  const lastPlayableByClef = new Map<StaffClef, ExerciseNote>();
  for (const measure of measures) {
    for (const note of measure.notes) {
      if (note.rest) continue;
      const prev = lastPlayableByClef.get(measure.clef);
      if (note.tieStop && !(prev && prev.tieStart && prev.midi === note.midi)) {
        note.tieStop = false;
      }
      lastPlayableByClef.set(measure.clef, note);
    }
  }

  // Tied continuations merge into the previous note, so they are not separately playable.
  totalNotes = measures.reduce(
    (acc, measure) => acc + measure.notes.filter((note) => !note.rest && !note.tieStop).length,
    0
  );

  return {
    id: `imported-cxml-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    measures,
    clef,
    keySignature: parsedKeySignature,
    timeSignature: parsedTimeSignature,
    difficulty: options.difficulty,
    tempo: parsedTempo,
    totalNotes,
    totalBeats: selectedMeasureNumbers.length * beatsPerMeasure,
    beatDurationMs,
  };
}
